import React, { useEffect, useRef, useState } from 'react';
import { supabase } from '../supabaseClient';
import { formatSnapshotTime } from '../utils/dateUtils';
import { fetchWithBackendFallback } from '../utils/backendApi';

function Dashboard({ user, watchlistCoin = null, showPrevious = false, clearAnalysisData = false }) {
  const [coin, setCoin] = useState(watchlistCoin || '');
  const [coinFilter, setCoinFilter] = useState(watchlistCoin || '');
  const [coinList, setCoinList] = useState([]);
  const [loadingCoins, setLoadingCoins] = useState(true);
  const [showDropdown, setShowDropdown] = useState(false);

  const [analysisData, setAnalysisData] = useState(null);
  const [snapshotTime, setSnapshotTime] = useState(null);
  const [actualAnalysisTime, setActualAnalysisTime] = useState(null);
  const [expandedTimeframes, setExpandedTimeframes] = useState({});

  const [loading, setLoading] = useState(false);
  const [loadingStatus, setLoadingStatus] = useState('');
  const [error, setError] = useState('');

  const [analysisStartTime, setAnalysisStartTime] = useState(null);
  const [currentAnalysisTime, setCurrentAnalysisTime] = useState(0);
  const [estimatedTotalTime, setEstimatedTotalTime] = useState(90);

  const [isInWatchlist, setIsInWatchlist] = useState(false);
  const [addingToWatchlist, setAddingToWatchlist] = useState(false);

  const initialFetchRef = useRef(false);
  const ANALYSIS_STORAGE_KEY = 'crypnai_analysis_latest_data';

  const saveAnalysisToStorage = (data) => {
    try {
      const payload = { ...data, timestamp: Date.now(), userId: user?.id };
      localStorage.setItem(ANALYSIS_STORAGE_KEY, JSON.stringify(payload));
    } catch (storageError) {
      console.error('Failed to save analysis data:', storageError);
    }
  };

  const loadAnalysisFromStorage = () => {
    try {
      const savedData = localStorage.getItem(ANALYSIS_STORAGE_KEY);
      if (!savedData) return null;
      const parsedData = JSON.parse(savedData);
      const isValid =
        parsedData.userId === user?.id &&
        Date.now() - parsedData.timestamp < 24 * 60 * 60 * 1000;
      if (!isValid) {
        localStorage.removeItem(ANALYSIS_STORAGE_KEY);
        return null;
      }
      return parsedData;
    } catch (storageError) {
      console.error('Failed to load analysis data:', storageError);
      localStorage.removeItem(ANALYSIS_STORAGE_KEY);
      return null;
    }
  };

  const clearAllAnalysisStates = () => {
    setAnalysisData(null);
    setSnapshotTime(null);
    setActualAnalysisTime(null);
    setError('');
    setLoadingStatus('');
    setExpandedTimeframes({});
    if (!watchlistCoin) {
      setCoin('');
      setCoinFilter('');
    }
    localStorage.removeItem(ANALYSIS_STORAGE_KEY);
  };

  const generateKey = () =>
    Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatAnalysisTime = (seconds) => {
    if (!seconds && seconds !== 0) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return mins > 0 ? `${mins}m ${secs}s` : `${secs}s`;
  };

  const numberOrDash = (value, decimals = 2, prefix = '') => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return 'N/A';
    return `${prefix}${Number(value).toFixed(decimals)}`;
  };

  const pctOrDash = (value, decimals = 2) => {
    if (value === null || value === undefined || Number.isNaN(Number(value))) return 'N/A';
    return `${Number(value).toFixed(decimals)}%`;
  };

  const recommendationClass = (action) => {
    if (action === 'LONG') return 'signal-pill signal-pill-long';
    if (action === 'SHORT') return 'signal-pill signal-pill-short';
    return 'signal-pill signal-pill-wait';
  };

  const calculateAverageTradeConfidence = (results) => {
    const values = (results?.timeframe_analyses || [])
      .map((item) => Number(item?.consensus?.trade_confidence))
      .filter((val) => Number.isFinite(val));
    if (values.length === 0) return null;
    const avg = values.reduce((sum, val) => sum + val, 0) / values.length;
    return Number(avg.toFixed(2));
  };

  const pollStatus = async (backendUrl, key, symbol, maxAttempts = 120, interval = 5000) => {
    let attempts = 0;
    while (attempts < maxAttempts) {
      const statusResponse = await fetch(`${backendUrl}/status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, coin: symbol })
      });

      if (!statusResponse.ok) {
        throw new Error('Failed to check analysis status');
      }

      const statusData = await statusResponse.json();
      if (statusData.status === 'complete') return statusData.results;
      if (statusData.status === 'error') {
        throw new Error(statusData.error || 'Analysis failed');
      }

      setLoadingStatus(statusData.progress || 'Processing...');
      await new Promise((resolve) => setTimeout(resolve, interval));
      attempts += 1;
    }

    throw new Error('Analysis timed out');
  };

  const checkWatchlistStatus = async (coinSymbol) => {
    if (!user || !coinSymbol) {
      setIsInWatchlist(false);
      return;
    }

    try {
      const { data, error: watchlistError } = await supabase
        .from('llm_analysis')
        .select('id')
        .eq('user_id', user.id)
        .eq('coin', coinSymbol.toUpperCase())
        .limit(1);

      if (watchlistError) {
        console.error('Error checking watchlist status:', watchlistError);
        setIsInWatchlist(false);
        return;
      }

      setIsInWatchlist(Boolean(data && data.length > 0));
    } catch (watchlistError) {
      console.error('Error checking watchlist status:', watchlistError);
      setIsInWatchlist(false);
    }
  };

  const toggleWatchlist = async () => {
    const selectedCoin = (coin || coinFilter).toUpperCase();
    if (!user || !selectedCoin || !analysisData) return;

    setAddingToWatchlist(true);
    setError('');

    try {
      if (isInWatchlist) {
        const { error: removeError } = await supabase
          .from('llm_analysis')
          .delete()
          .eq('user_id', user.id)
          .eq('coin', selectedCoin);

        if (removeError) throw removeError;
      } else {
        const payload = {
          user_id: user.id,
          coin: selectedCoin,
          version: analysisData.version || 'consensus',
          timeframes: analysisData.timeframes || [],
          llm_models: analysisData.llm_models || [],
          timeframe_analyses: analysisData.timeframe_analyses || [],
          analysis_time: analysisData.analysis_time || 0,
          majority_recommendation: analysisData.majority_recommendation || 'WAIT',
          current_price: analysisData.current_price || null,
          analysis_mode: analysisData.analysis_mode || 'debate_consensus',
          consensus_model: analysisData.consensus_model || null,
          consensus_trade_confidence:
            analysisData.consensus_trade_confidence ?? calculateAverageTradeConfidence(analysisData),
          consensus_trade_confidence_delta: null,
          model_telemetry: analysisData.model_telemetry || {},
          analysis_telemetry: analysisData.analysis_telemetry || {},
          confidence_deltas: analysisData.confidence_deltas || {
            overall_trade_confidence_delta: null,
            previous_average_trade_confidence: null,
            latest_average_trade_confidence:
              analysisData.consensus_trade_confidence ?? calculateAverageTradeConfidence(analysisData),
            timeframe_trade_confidence_delta: {}
          },
          snapshot_time: new Date().toISOString(),
          latest_sync: analysisData,
          previous_sync: null,
          change_detected: false
        };

        const { error: insertError } = await supabase.from('llm_analysis').insert(payload);
        if (insertError) throw insertError;
      }

      await checkWatchlistStatus(selectedCoin);
    } catch (watchlistError) {
      console.error('Error toggling watchlist:', watchlistError);
      setError(`Watchlist update failed: ${watchlistError.message}`);
    } finally {
      setAddingToWatchlist(false);
    }
  };

  const toggleTimeframe = (timeframe) => {
    setExpandedTimeframes((prev) => ({ ...prev, [timeframe]: !prev[timeframe] }));
  };

  const handleCoinSelect = (selectedCoin) => {
    setCoin(selectedCoin);
    setCoinFilter(selectedCoin);
    setShowDropdown(false);
  };

  const handleCoinFilterChange = (event) => {
    const value = event.target.value.toUpperCase();
    setCoinFilter(value);
    setCoin(value);
    setShowDropdown(true);
  };

  const handleAnalyze = async () => {
    const selectedCoin = coin || coinFilter;
    if (!selectedCoin) {
      setError('Please enter a cryptocurrency symbol');
      return;
    }

    setLoading(true);
    setError('');
    setLoadingStatus('Initializing consensus analysis...');
    clearAllAnalysisStates();

    const startTimeMs = Date.now();
    setAnalysisStartTime(startTimeMs);
    const key = generateKey();

    try {
      const { response: startResponse, backendUrl } = await fetchWithBackendFallback('/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ coin: selectedCoin, key, user_id: user?.id })
      });

      if (!startResponse.ok) {
        const startError = await startResponse.json().catch(() => ({}));
        throw new Error(startError.detail || 'Failed to start analysis');
      }

      const startData = await startResponse.json().catch(() => ({}));
      const modelCount = startData.model_count || 3;
      setEstimatedTotalTime(25 * modelCount + 15);

      const results = await pollStatus(backendUrl, key, selectedCoin);
      const endTimeMs = Date.now();
      const analysisTime = Math.floor((endTimeMs - startTimeMs) / 1000);
      const snapshot = new Date().toISOString();

      const enrichedResults = {
        ...results,
        version: results?.version || 'consensus',
        coin: selectedCoin
      };

      setAnalysisData(enrichedResults);
      setSnapshotTime(snapshot);
      setActualAnalysisTime(analysisTime);
      setLoadingStatus('');
      setCoin(selectedCoin);
      setCoinFilter(selectedCoin);

      if (enrichedResults?.timeframe_analyses?.length > 0) {
        setExpandedTimeframes({ [enrichedResults.timeframe_analyses[0].timeframe]: true });
      }

      saveAnalysisToStorage({
        analysisData: enrichedResults,
        snapshotTime: snapshot,
        actualAnalysisTime: analysisTime,
        coin: selectedCoin,
        coinFilter: selectedCoin
      });

      await checkWatchlistStatus(selectedCoin);

      await fetchWithBackendFallback('/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, coin: selectedCoin })
      }).catch(() => {});
    } catch (analyzeError) {
      setError(analyzeError.message || 'Analysis failed');
      setLoadingStatus('');
    } finally {
      setLoading(false);
      setAnalysisStartTime(null);
    }
  };

  useEffect(() => {
    if (initialFetchRef.current) return;
    initialFetchRef.current = true;

    const fetchCoins = async () => {
      setLoadingCoins(true);
      try {
        const { response } = await fetchWithBackendFallback('/coinlist', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        if (!response.ok) {
          throw new Error('Failed to fetch coin list');
        }

        const data = await response.json();
        setCoinList(data.coins || []);
      } catch (coinError) {
        setError(`Failed to load coin list: ${coinError.message}`);
      } finally {
        setLoadingCoins(false);
      }
    };

    fetchCoins();
  }, []);

  useEffect(() => {
    if (watchlistCoin) {
      setCoin(watchlistCoin);
      setCoinFilter(watchlistCoin);
      return;
    }

    if (clearAnalysisData) {
      clearAllAnalysisStates();
      return;
    }

    const saved = loadAnalysisFromStorage();
    if (saved) {
      setAnalysisData(saved.analysisData || null);
      setSnapshotTime(saved.snapshotTime || null);
      setActualAnalysisTime(saved.actualAnalysisTime || null);
      setCoin(saved.coin || '');
      setCoinFilter(saved.coinFilter || '');
    }
  }, [watchlistCoin, clearAnalysisData]);

  useEffect(() => {
    const selectedCoin = coin || coinFilter;
    if (selectedCoin && user) {
      checkWatchlistStatus(selectedCoin);
    }
  }, [coin, coinFilter, user]);

  useEffect(() => {
    let interval;
    if (loading && analysisStartTime) {
      interval = setInterval(() => {
        const elapsed = Math.floor((Date.now() - analysisStartTime) / 1000);
        setCurrentAnalysisTime(elapsed);
      }, 1000);
    } else {
      setCurrentAnalysisTime(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [loading, analysisStartTime]);

  useEffect(() => {
    const loadSavedAnalysis = async () => {
      if (!watchlistCoin || !user) return;

      const urlParams = new URLSearchParams(window.location.search);
      const shouldShowPrevious = urlParams.get('previous') === 'true' || showPrevious;

      const { data, error: dbError } = await supabase
        .from('llm_analysis')
        .select('*')
        .eq('user_id', user.id)
        .eq('coin', watchlistCoin.toUpperCase())
        .order('snapshot_time', { ascending: false })
        .limit(1)
        .single();

      if (dbError || !data) return;

      const syncData =
        shouldShowPrevious && data.previous_sync
          ? data.previous_sync
          : data.latest_sync || {
              version: data.version,
              timeframes: data.timeframes,
              llm_models: data.llm_models,
              timeframe_analyses: data.timeframe_analyses,
              analysis_time: data.analysis_time,
              majority_recommendation: data.majority_recommendation,
              current_price: data.current_price
            };

      const merged = {
        ...syncData,
        coin: watchlistCoin,
        version: syncData.version || 'consensus'
      };

      setAnalysisData(merged);
      setSnapshotTime(syncData.snapshot_time || data.snapshot_time || null);
      setActualAnalysisTime(syncData.analysis_time || data.analysis_time || null);
      if (merged.timeframe_analyses?.length > 0) {
        setExpandedTimeframes({ [merged.timeframe_analyses[0].timeframe]: true });
      }
    };

    loadSavedAnalysis();
  }, [watchlistCoin, user, showPrevious]);

  const filteredCoins = coinList.filter((coinName) =>
    coinName.toLowerCase().includes((coinFilter || '').toLowerCase())
  );

  return (
    <main className="app-page space-y-6">
      <section className="card">
        <div className="page-head">
          <div>
            <p className="eyebrow">CrypnAI</p>
            <h1 className="page-title">Trading dashboard</h1>
            <p className="page-subtitle">
              We compute the market context first, run it through the model panel, and return one final call for each timeframe.
            </p>
          </div>
          <div className="pill-row">
            <span className="soft-pill">Timeframes: 5m, 15m, 1h, 4h, 1d</span>
            <span className="soft-pill">Provider: OpenRouter</span>
          </div>
        </div>

        {loadingCoins ? (
          <div className="status-card">
            <div className="spinner" />
            <div>
              <h3>Loading assets</h3>
              <p>Getting the latest symbols from Hyperliquid...</p>
            </div>
          </div>
        ) : (
          <div className="control-grid">
            <div className="relative">
              <label className="control-label">Cryptocurrency</label>
              <input
                type="text"
                value={coinFilter}
                onChange={handleCoinFilterChange}
                onFocus={() => setShowDropdown(true)}
                onBlur={() => setTimeout(() => setShowDropdown(false), 150)}
                placeholder="Search symbol (BTC, ETH, SOL...)"
                className="input-field"
                autoComplete="off"
              />
              {showDropdown && filteredCoins.length > 0 && (
                <div className="coin-dropdown">
                  {filteredCoins.slice(0, 12).map((coinName) => (
                    <button
                      type="button"
                      key={coinName}
                      onClick={() => handleCoinSelect(coinName)}
                      className="coin-option"
                    >
                      <span className="coin-avatar">{coinName.slice(0, 1)}</span>
                      <span>{coinName}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <button
              onClick={handleAnalyze}
              disabled={loading || !coin || loadingCoins}
              className="btn-primary h-[50px]"
            >
              {loading ? 'Running analysis...' : 'Run analysis'}
            </button>
          </div>
        )}
      </section>

      {snapshotTime && analysisData && (
        <section className="card">
          <div className="snapshot-strip">
            <div>
              <p className="snapshot-title">Snapshot from {formatSnapshotTime(snapshotTime, Boolean(watchlistCoin))}</p>
              <p className="snapshot-meta">
                Ran in {formatAnalysisTime(actualAnalysisTime)} across {analysisData.timeframes?.length || 0} timeframes using {analysisData.llm_models?.length || 0} models
              </p>
            </div>
            <button
              onClick={toggleWatchlist}
              disabled={addingToWatchlist}
              className={isInWatchlist ? 'btn-secondary' : 'btn-primary'}
            >
              {addingToWatchlist
                ? 'Updating...'
                : isInWatchlist
                ? 'Remove From Watchlist'
                : 'Add To Watchlist'}
            </button>
          </div>
        </section>
      )}

      {loading && (
        <section className="card loading-shell">
          <div className="status-card mb-3">
            <div className="spinner" />
            <div>
              <h3>Running analysis</h3>
              <p>{loadingStatus || 'Computing the data and collecting model calls...'}</p>
            </div>
            <div className="timer-wrap">
              <span>{formatTime(currentAnalysisTime)}</span>
              <small>Est. {formatTime(estimatedTotalTime)}</small>
            </div>
          </div>
          <div className="progress-track">
            <div
              className="progress-fill"
              style={{ width: `${Math.min((currentAnalysisTime / estimatedTotalTime) * 100, 100)}%` }}
            />
          </div>
        </section>
      )}

      {error && (
        <section className="error-banner">
          <h3>Analysis Error</h3>
          <p>{error}</p>
        </section>
      )}

      {analysisData?.timeframe_analyses?.length > 0 ? (
        <section className="space-y-5">
          {analysisData.timeframe_analyses.map((tfData, idx) => {
            const consensus = tfData.consensus || {};
            const recommendation =
              consensus.recommendation || tfData.majority_recommendation || 'WAIT';
            const technical = tfData.technical_snapshot || {};
            const price = technical.price || {};
            const trend = technical.trend || {};
            const momentum = technical.momentum || {};
            const trendFollowing = technical.trend_following || {};
            const volatility = technical.volatility || {};
            const volumeFlow = technical.volume_flow || {};
            const levels = technical.levels || {};
            const setup = technical.deterministic_trade_setup || {};
            const modelVotes = (tfData.model_votes || []).filter((vote) => vote.status === 'success');

            return (
              <article className="card" key={`${tfData.timeframe}-${idx}`}>
                <button
                  type="button"
                  className="tf-header"
                  onClick={() => toggleTimeframe(tfData.timeframe)}
                >
                  <div className="tf-heading-wrap">
                    <h2>
                      {analysisData.coin} {String(tfData.timeframe).toUpperCase()} Consensus
                    </h2>
                    <span className={recommendationClass(recommendation)}>{recommendation}</span>
                  </div>
                  <span className="tf-chevron">
                    {expandedTimeframes[tfData.timeframe] ? '−' : '+'}
                  </span>
                </button>

                {expandedTimeframes[tfData.timeframe] && (
                  <div className="space-y-4 mt-4">
                    <div className="metrics-grid">
                      <div className="metric-box"><span>Current Price</span><strong>{numberOrDash(price.current, 4, '$')}</strong></div>
                      <div className="metric-box"><span>Trade Confidence</span><strong>{pctOrDash(consensus.trade_confidence, 0)}</strong></div>
                      <div className="metric-box"><span>Bullish Confidence</span><strong>{pctOrDash(consensus.bullish_confidence, 0)}</strong></div>
                      <div className="metric-box"><span>Bearish Confidence</span><strong>{pctOrDash(consensus.bearish_confidence, 0)}</strong></div>
                      <div className="metric-box"><span>Risk Level</span><strong>{consensus.risk_level || 'N/A'}</strong></div>
                      <div className="metric-box"><span>Regime</span><strong>{trend.market_regime || trend.label || 'N/A'}</strong></div>
                      <div className="metric-box"><span>Entry</span><strong>{numberOrDash(consensus.entry_price, 4, '$')}</strong></div>
                      <div className="metric-box"><span>TP / SL</span><strong>{`${numberOrDash(consensus.take_profit, 4, '$')} / ${numberOrDash(consensus.stop_loss, 4, '$')}`}</strong></div>
                    </div>

                    <div className="panel-grid">
                      <div className="insight-panel">
                        <h4>Technical Snapshot</h4>
                        <ul>
                          <li>Price Change (5/20/50 bars): {pctOrDash(price.change_5bars_pct)} / {pctOrDash(price.change_20bars_pct)} / {pctOrDash(price.change_50bars_pct)}</li>
                          <li>Trend: {trend.label || 'N/A'} · Strength: {trend.strength || 'N/A'}</li>
                          <li>RSI: {numberOrDash(momentum.rsi, 2)}</li>
                          <li>MACD Histogram: {numberOrDash((momentum.macd || {}).histogram, 4)}</li>
                          <li>DMI ADX: {numberOrDash((momentum.dmi || {}).adx, 2)}</li>
                          <li>VWAP: {numberOrDash(trendFollowing.vwap, 4, '$')}</li>
                        </ul>
                      </div>

                      <div className="insight-panel">
                        <h4>Market Data And Volatility</h4>
                        <ul>
                          <li>ATR: {numberOrDash(volatility.atr, 4)}</li>
                          <li>Volume Trend: {(volumeFlow.volume_profile_summary || {}).volume_trend || 'N/A'}</li>
                          <li>Current / Avg Volume: {numberOrDash((volumeFlow.volume_profile_summary || {}).current_volume, 0)} / {numberOrDash((volumeFlow.volume_profile_summary || {}).average_volume, 0)}</li>
                          <li>OBV: {numberOrDash(volumeFlow.obv, 0)}</li>
                          <li>CMF: {numberOrDash(volumeFlow.chaikin_money_flow, 4)}</li>
                          <li>Channel Pattern: {((technical.patterns || {}).channel_pattern || {}).pattern || 'N/A'}</li>
                        </ul>
                      </div>

                      <div className="insight-panel">
                        <h4>Deterministic Trade Setup</h4>
                        <ul>
                          <li>Model-Free Signal: {setup.recommendation || 'N/A'}</li>
                          <li>Entry: {numberOrDash(setup.entry_price, 4, '$')}</li>
                          <li>Take Profit: {numberOrDash(setup.take_profit, 4, '$')}</li>
                          <li>Stop Loss: {numberOrDash(setup.stop_loss, 4, '$')}</li>
                          <li>Supports: {((levels.support_resistance || {}).support_levels || []).slice(0, 3).map((val) => `$${val}`).join(', ') || 'N/A'}</li>
                          <li>Resistances: {((levels.support_resistance || {}).resistance_levels || []).slice(0, 3).map((val) => `$${val}`).join(', ') || 'N/A'}</li>
                        </ul>
                      </div>
                    </div>

                    <div className="insight-panel">
                      <h4>Debate Summary</h4>
                      <p>{consensus.debate_summary || 'Final call generated from the shared data set and model review.'}</p>
                    </div>

                    <div className="panel-grid">
                      <div className="insight-panel">
                        <h4>Thesis</h4>
                        <ul>
                          {(consensus.thesis || []).map((point, pointIdx) => (
                            <li key={`thesis-${pointIdx}`}>{point}</li>
                          ))}
                        </ul>
                      </div>
                      <div className="insight-panel">
                        <h4>Risks</h4>
                        <ul>
                          {(consensus.risks || []).map((point, pointIdx) => (
                            <li key={`risk-${pointIdx}`}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    </div>

                    {(consensus.invalidations || []).length > 0 && (
                      <div className="insight-panel">
                        <h4>Invalidations</h4>
                        <ul>
                          {(consensus.invalidations || []).map((point, pointIdx) => (
                            <li key={`inv-${pointIdx}`}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}

                    <div className="insight-panel">
                      <h4>Model Vote Matrix</h4>
                      <div className="vote-grid">
                        {modelVotes.map((vote, voteIdx) => (
                          <div key={`vote-${voteIdx}`} className="vote-card">
                            <div className="vote-head">
                              <span>{vote.model}</span>
                              <span className={recommendationClass(vote.recommendation)}>{vote.recommendation}</span>
                            </div>
                            <p>
                              Trade {vote.trade_confidence}% · Bull {vote.bullish_confidence}% · Bear {vote.bearish_confidence}%
                            </p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </section>
      ) : (
        analysisData && (
          <section className="card">
            <h3 className="text-text-light font-semibold mb-2">No timeframe data found</h3>
            <pre className="text-xs overflow-auto">{JSON.stringify(analysisData, null, 2)}</pre>
          </section>
        )
      )}
    </main>
  );
}

export default Dashboard;
